import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Container } from "reactstrap";
import { useParams } from "react-router-dom";

import { PageTitles } from "../../../constants";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { RoutingTabs } from "../../../components/RoutingTabs";
import { LoaderFallbackUI } from "../../../components/LoaderFallbackUI";
import { adminUserOverview } from "../../../api/api.service";

import Biography from "./Biography";
import Overview from "./Overview";
import Resets from "./Resets";
import PlanPayments from "./PlanPayments";
import Affirmations from "./Affirmations";
import MoodHistory from "./MoodHistory";
import BlsSessions from "./BlsSessions";
import WellnessTests from "./WellnessTests";
import typography from "../typography";

const tabPath = (slug) => `${APP_ROUTES.CUSTOMER_DETAILS}/:id/${slug}`;

/**
 * The tabs this person's record can fill.
 *
 * Overview, Resets and Plan & Payments describe every teacher. The four
 * mobile-app tabs are shown only when this account actually has that data —
 * a teacher-portal account never writes affirmations or wellness tests, so for
 * most people those tabs would only ever read "Data not found!".
 */
export const buildTabs = (overview) => {
  const mobile = overview?.mobileData ?? {};
  return [
    {
      identifier: "overview",
      title: typography.TABS_OVERVIEW,
      component: <Overview overview={overview} />,
      defaultActive: true,
    },
    {
      identifier: "resets",
      title: typography.TABS_RESETS,
      component: <Resets timezone={overview?.profile?.timezone} />,
    },
    {
      identifier: "plan",
      title: typography.TABS_PLAN_PAYMENTS,
      component: <PlanPayments />,
    },
    mobile.blsSessions > 0 && {
      identifier: "bls-sessions",
      title: typography.TABS_BLS_SESSIONS,
      component: <BlsSessions />,
    },
    mobile.moods > 0 && {
      identifier: "mood",
      title: typography.TABS_MOOD,
      component: <MoodHistory />,
    },
    mobile.affirmations > 0 && {
      identifier: "affirmations",
      title: typography.TABS_AFFIRMATIONS,
      component: <Affirmations />,
    },
    mobile.wellnessTests > 0 && {
      identifier: "wellness-tests",
      title: typography.TABS_WELLNESS_TESTS,
      component: <WellnessTests />,
    },
  ]
    .filter(Boolean)
    .map((tab, index) => ({
      ...tab,
      id: index + 1,
      path: tabPath(tab.identifier),
    }));
};

export default function Details() {
  const { id } = useParams();
  const [overview, setOverview] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOverview(null);
    setFailed(false);
    adminUserOverview(id)
      .then((res) => {
        if (!cancelled) setOverview(res?.data ?? null);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const onStatusChanged = (status) =>
    setOverview((prev) =>
      prev ? { ...prev, profile: { ...prev.profile, status } } : prev,
    );

  return (
    <div className="page-content">
      <Helmet>
        <title>{PageTitles.CUSTOMER_DETAILS}</title>
      </Helmet>
      <Container fluid className="customer-details">
        {failed ? (
          <p className="cd-empty">This customer could not be loaded.</p>
        ) : null}
        {!overview && !failed ? <LoaderFallbackUI isLoader /> : null}
        {overview ? (
          <>
            <Biography overview={overview} onStatusChanged={onStatusChanged} />
            {/* Tabs wait for the overview: which tabs exist depends on it, and
                RoutingTabs redirects to the default tab on first render. */}
            <RoutingTabs
              config={{ tabsConfig: buildTabs(overview), param: { id } }}
              redirectionFromRoute={`${APP_ROUTES.CUSTOMER_DETAILS}/:id`}
            />
          </>
        ) : null}
      </Container>
    </div>
  );
}
